import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import RwmFlowApplyForm from './RwmFlowApplyForm';
import JobTag from './JobTag';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost } from '../../services/api';
import { avatarColor, employeeCountLabel } from '../../utils/companyHelpers';

function currencySymbol(code) {
  if (code === 'USD') return '$';
  if (code === 'EUR') return '€';
  if (code === 'GBP') return '£';
  return `${code} `;
}

function formatSalaryDetail(min, max, currency, period) {
  const sym = currencySymbol(currency);
  const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const a = nf.format(Number(min));
  const b = nf.format(Number(max));
  const periodPart =
    {
      annual: '/ year',
      monthly: '/ month',
      hourly: '/ hour',
      yearly: '/ year',
      project: '',
    }[period] || '/ month';
  return `${sym}${a}–${sym}${b} ${periodPart}`.trim();
}

function formatEmploymentTitle(value) {
  if (!value) return '';
  return String(value)
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

function formatPostedShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatPostedLong(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatSeniority(value) {
  const labels = {
    entry: 'Entry Level',
    mid: 'Mid-Level',
    senior: 'Senior',
    lead: 'Lead / Principal',
  };
  if (Array.isArray(value) && value.length > 0) {
    return value.map((l) => labels[l] || l).join(' / ');
  }
  if (!value) return '—';
  return String(value)
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function mapEmploymentType(value) {
  const normalized = String(value || '').toLowerCase().replace(/_/g, '-');
  const map = {
    'full-time': 'FULL_TIME',
    'part-time': 'PART_TIME',
    contract: 'CONTRACTOR',
    freelance: 'CONTRACTOR',
  };
  return map[normalized] || 'OTHER';
}

function mapSalaryPeriod(value) {
  const map = {
    hourly: 'HOUR',
    daily: 'DAY',
    weekly: 'WEEK',
    monthly: 'MONTH',
    yearly: 'YEAR',
    annual: 'YEAR',
  };
  return map[String(value || '').toLowerCase()] || 'YEAR';
}

function getApplyConfig(job) {
  const method = job?.application_method;
  if (method === 'rwm_flow') {
    return null;
  }
  if (method === 'external_url') {
    const url = job?.application_url;
    return {
      label: 'Apply on Company Site',
      href: url || '#',
      helper: "You'll be taken to the company's website.",
      isRouterLink: false,
      newTab: true,
      disabled: !url,
    };
  }
  if (method === 'email') {
    const email = job?.application_email;
    return {
      label: 'Apply via Email',
      href: email ? `mailto:${email}` : '#',
      helper: 'Opens your email client.',
      isRouterLink: false,
      newTab: false,
      disabled: !email,
    };
  }
  return {
    label: 'Apply',
    href: '#',
    helper: '',
    isRouterLink: false,
    newTab: false,
    disabled: true,
  };
}

function ApplyActionButton({ cfg, dataTestId = 'rwm-apply-button' }) {
  const className = 'w-100 btn-lg text-white';
  if (cfg.disabled) {
    return (
      <Button variant="info" className={className} disabled {...(dataTestId ? { 'data-testid': dataTestId } : {})}>
        {cfg.label}
      </Button>
    );
  }
  if (cfg.isRouterLink) {
    return (
      <Button variant="info" className={className} as={Link} to={cfg.href} {...(dataTestId ? { 'data-testid': dataTestId } : {})}>
        {cfg.label}
      </Button>
    );
  }
  return (
    <Button
      variant="info"
      className={className}
      as="a"
      href={cfg.href}
      target={cfg.newTab ? '_blank' : undefined}
      rel={cfg.newTab ? 'noopener noreferrer' : undefined}
      {...(dataTestId ? { 'data-testid': dataTestId } : {})}
    >
      {cfg.label}
    </Button>
  );
}

function ApplyCardContent({ job, slug, showHelper, applyButtonTestId }) {
  if (job?.application_method === 'rwm_flow') {
    return <RwmFlowApplyForm jobSlug={slug} />;
  }

  const cfg = getApplyConfig(job);
  if (!cfg) {
    return null;
  }

  return (
    <>
      <ApplyActionButton cfg={cfg} dataTestId={applyButtonTestId} />
      {showHelper && cfg.helper ? (
        <p className="small text-muted mb-0 mt-2">{cfg.helper}</p>
      ) : null}
    </>
  );
}

function HideCompanyControl({ companyId, token, isCandidate }) {
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!isCandidate || !token || !companyId) {
      setChecking(false);
      return undefined;
    }

    let cancelled = false;

    async function checkHidden() {
      try {
        const res = await apiGet('/api/v1/candidate/hidden-companies', token);
        if (!res.ok) return;
        const json = await res.json();
        const ids = (json.data ?? []).map((item) => item.id);
        if (!cancelled) {
          setHidden(ids.includes(companyId));
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    checkHidden();

    return () => {
      cancelled = true;
    };
  }, [isCandidate, token, companyId]);

  if (!isCandidate || !companyId || checking) {
    return null;
  }

  const handleHide = async () => {
    setLoading(true);
    try {
      const res = await apiPost(`/api/v1/candidate/companies/${companyId}/hide`, {}, token);
      if (res.ok) {
        setHidden(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (hidden) {
    return (
      <p className="small text-muted mb-0 mt-3" data-testid="rwm-job-detail-company-hidden">
        Hidden — jobs from this company won&apos;t appear in your search
      </p>
    );
  }

  return (
    <button
      type="button"
      className="btn btn-link btn-sm p-0 mt-3 rwm-job-detail__browse-link"
      onClick={handleHide}
      disabled={loading}
      data-testid="rwm-job-detail-hide-company"
    >
      {loading ? 'Hiding…' : 'Hide jobs from this company'}
    </button>
  );
}

function JobDetailSkeleton() {
  return (
    <div className="container-fluid px-3 px-lg-4">
      <Row className="g-4">
        <Col lg={8}>
          <div className="rwm-browse-jobs__skeleton-card mb-3">
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--lg mb-2" />
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--md mb-3" />
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--sm" />
          </div>
          <div className="rwm-browse-jobs__skeleton-card">
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--md mb-2" />
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--sm mb-2" />
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--sm" />
          </div>
        </Col>
        <Col lg={4}>
          <div className="rwm-browse-jobs__skeleton-card mb-3">
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--lg mb-2" />
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--md" />
          </div>
          <div className="rwm-browse-jobs__skeleton-card">
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--sm mb-2" />
            <div className="rwm-browse-jobs__skeleton-line rwm-browse-jobs__skeleton-line--sm" />
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isCandidate = user?.role === 'candidate';
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const descriptionRef = useRef(null);

  const apiBase = useMemo(
    () => (process.env.REACT_APP_API_URL || '').replace(/\/$/, ''),
    []
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!apiBase || !slug) {
        setLoading(false);
        setNotFound(true);
        setJob(null);
        return;
      }
      setLoading(true);
      setNotFound(false);
      setJob(null);
      try {
        const res = await fetch(`${apiBase}/api/v1/jobs/${encodeURIComponent(slug)}`, {
          headers: { Accept: 'application/json' },
          redirect: 'manual',
        });

        if (res.status === 301) {
          const data = await res.json();
          const target = typeof data.redirect === 'string' ? data.redirect : null;
          if (target && !cancelled) {
            navigate(target, { replace: true });
          }
          return;
        }

        if (!res.ok) {
          if (!cancelled) {
            setNotFound(true);
            setJob(null);
          }
          return;
        }

        const json = await res.json();
        const payload = json.data;
        if (!cancelled) {
          setJob(payload && typeof payload === 'object' ? payload : null);
          setNotFound(!payload);
        }
      } catch {
        if (!cancelled) {
          setNotFound(true);
          setJob(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [apiBase, slug, navigate]);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return undefined;
    const html = job?.description && String(job.description).trim();
    if (!html) {
      el.innerHTML = '';
      return undefined;
    }
    el.innerHTML = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['p', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'br', 'h2', 'h3', 'h4', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
    return () => {
      el.innerHTML = '';
    };
  }, [job?.description]);

  useEffect(() => {
    if (!job) return undefined;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: job.title,
      description: job.description,
      datePosted: job.approved_at
        ? new Date(job.approved_at).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      validThrough: job.expires_at
        ? new Date(job.expires_at).toISOString().split('T')[0]
        : null,
      employmentType: mapEmploymentType(job.employment_type),
      jobLocationType: 'TELECOMMUTE',
      applicantLocationRequirements: job.location_country
        ? {
            '@type': 'Country',
            name: job.location_country,
          }
        : {
            '@type': 'AdministrativeArea',
            name: job.location_eligibility || 'Worldwide',
          },
      hiringOrganization: {
        '@type': 'Organization',
        name: job.company?.name || '',
        sameAs: job.company?.website || undefined,
      },
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: job.salary_currency || 'EUR',
        value: {
          '@type': 'QuantitativeValue',
          minValue: job.salary_min,
          maxValue: job.salary_max,
          unitText: mapSalaryPeriod(job.salary_period),
        },
      },
    };

    if (!job.expires_at) {
      delete schema.validThrough;
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'job-posting-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('job-posting-schema');
      if (existing) existing.remove();
    };
  }, [job]);

  if (loading) {
    return (
      <Container>
        <div className="rwm-job-detail bg-white py-4">
          <JobDetailSkeleton />
        </div>
      </Container>
    );
  }

  if (notFound || !job) {
    return (
      <Container className="py-5 text-center" data-testid="rwm-job-not-found">
        <p className="text-muted mb-3">This job listing is no longer available.</p>
        <Link to="/jobs" className="rwm-job-detail__browse-link text-decoration-none">
          Browse all remote jobs →
        </Link>
      </Container>
    );
  }

  const company = job.company || {};
  const category = job.category || {};
  const logoUrl = company.logo_url || null;
  const initial = (company.name && company.name.trim()[0]) || '?';
  const salaryText = formatSalaryDetail(
    job.salary_min,
    job.salary_max,
    job.salary_currency,
    job.salary_period
  );
  const website = company.website;
  const companyNameEl =
    website && String(website).trim() !== '' ? (
      <a
        href={website.startsWith('http') ? website : `https://${website}`}
        target="_blank"
        rel="noopener noreferrer"
        className="rwm-job-detail__company-link text-decoration-none"
      >
        {company.name}
      </a>
    ) : (
      <span>{company.name}</span>
    );

  const locationLabel = job.location_country || job.location_eligibility || '';
  const locationLine = [job.location_type === 'remote' ? 'Remote worldwide' : 'Remote', locationLabel]
    .filter(Boolean)
    .join(' · ');

  return (
    <Container>
      <div className="rwm-job-detail bg-white py-4">
      <span
        data-testid="rwm-job-schema"
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      <div className="container-fluid px-3 px-lg-4">
        <Row className="g-4">
          <Col lg={8}>
            <Card
              className="border mb-4 rwm-job-detail__card"
              style={{ borderColor: 'var(--rwm-border)' }}
              data-testid="rwm-job-header"
            >
              <Card.Body className="p-4">
                <div className="d-flex gap-3 align-items-start">
                  <div className="flex-shrink-0" style={{ width: 48, height: 48 }}>
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={company.name || 'Company'}
                        width={48}
                        height={48}
                        className="rounded-circle"
                        style={{ objectFit: 'cover', width: 48, height: 48 }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold"
                        style={{
                          width: 48,
                          height: 48,
                          backgroundColor: avatarColor(company.name || ''),
                          fontSize: 18,
                        }}
                      >
                        {initial.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1 min-w-0">
                    <h1
                      className="h3 fw-semibold mb-2"
                      style={{ color: 'var(--rwm-dark)' }}
                      data-testid="rwm-job-title"
                    >
                      {job.title}
                    </h1>
                    <div className="mb-2" style={{ color: 'var(--rwm-slate)' }}>
                      {companyNameEl}
                    </div>
                    <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                      {job.employment_type ? (
                        <JobTag
                          type="employment"
                          value={job.employment_type}
                          className="badge rounded-pill rwm-browse-jobs__employment-badge"
                        >
                          {formatEmploymentTitle(job.employment_type)}
                        </JobTag>
                      ) : null}
                      {category.name && category.id ? (
                        <JobTag
                          type="category"
                          value={category.id}
                          className="badge rounded-pill rwm-browse-jobs__employment-badge"
                        >
                          {category.name}
                        </JobTag>
                      ) : category.name ? (
                        <span className="badge rounded-pill rwm-browse-jobs__employment-badge">{category.name}</span>
                      ) : null}
                      {job.is_featured ? (
                        <span className="badge rounded-pill rwm-browse-jobs__featured-badge" data-testid="rwm-featured-badge">
                          Featured
                        </span>
                      ) : null}
                    </div>
                    <div
                      className="badge rounded-pill rwm-job-detail__salary-pill rwm-salary-badge px-3 py-2"
                      data-testid="rwm-salary-badge"
                    >
                      {salaryText}
                    </div>
                    <p className="small text-muted mt-3 mb-0">
                      Posted on {formatPostedShort(job.approved_at)}
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <Card className="border rwm-job-detail__card" style={{ borderColor: 'var(--rwm-border)' }}>
              <Card.Body className="p-4">
                <h2 className="h5 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
                  About this role
                </h2>
                <div
                  ref={descriptionRef}
                  className="rwm-job-detail__description"
                  data-testid="rwm-job-description"
                />
              </Card.Body>
            </Card>

            {job.skills && job.skills.length > 0 ? (
              <Card
                className="border rwm-job-detail__card mt-4"
                style={{ borderColor: 'var(--rwm-border)' }}
                data-testid="rwm-job-detail-skills"
              >
                <Card.Body className="p-4">
                  <h2 className="h5 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
                    Required Skills
                  </h2>
                  <div className="d-flex flex-wrap gap-2">
                    {job.skills.map((skill) => (
                      <span
                        key={skill.id}
                        className="rwm-skill-badge--outline"
                        data-testid={`rwm-job-detail-skill-badge-${skill.id}`}
                      >
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            ) : null}

            {job.application_method !== 'rwm_flow' ? (
              <div className="d-lg-none mt-4">
                <Card
                  className="border rwm-job-detail__card"
                  style={{ borderColor: 'var(--rwm-border)' }}
                  data-testid="rwm-apply-card"
                >
                  <Card.Body className="p-4">
                    <ApplyCardContent job={job} slug={slug} showHelper applyButtonTestId={null} />
                  </Card.Body>
                </Card>
              </div>
            ) : null}

            <div className="d-lg-none mt-4">
              <Card className="border rwm-job-detail__card" style={{ borderColor: 'var(--rwm-border)' }} data-testid="rwm-job-info">
                <Card.Body className="p-4">
                  <h3 className="h6 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
                    Job Info
                  </h3>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>💰 Salary</span>
                    <span className="fw-medium text-end">{salaryText}</span>
                  </div>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>📋 Job Type</span>
                    <span className="text-end">{formatEmploymentTitle(job.employment_type)}</span>
                  </div>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>🎯 Seniority</span>
                    <span className="text-end">{formatSeniority(job.seniority_level)}</span>
                  </div>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>🌍 Location</span>
                    <span className="text-end">{locationLine}</span>
                  </div>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>📁 Category</span>
                    <span className="text-end">{category.name || '—'}</span>
                  </div>
                  <div className="rwm-job-detail__info-row">
                    <span>📅 Posted</span>
                    <span className="text-end">{formatPostedLong(job.approved_at)}</span>
                  </div>
                </Card.Body>
              </Card>
            </div>

            <div className="d-lg-none mt-4 mb-5">
              <Card className="border rwm-job-detail__card" style={{ borderColor: 'var(--rwm-border)' }} data-testid="rwm-company-card">
                <Card.Body className="p-4">
                  <h3 className="h6 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
                    About the Company
                  </h3>
                  <div className="d-flex gap-2 align-items-center mb-2">
                    <div style={{ width: 36, height: 36 }}>
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt=""
                          width={36}
                          height={36}
                          className="rounded-circle"
                          style={{ objectFit: 'cover', width: 36, height: 36 }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold small"
                          style={{
                            width: 36,
                            height: 36,
                            backgroundColor: avatarColor(company.name || ''),
                          }}
                        >
                          {initial.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="fw-medium">{company.name}</span>
                  </div>
                  {employeeCountLabel(company.employee_count) ? (
                    <p className="small text-muted mb-2">{employeeCountLabel(company.employee_count)}</p>
                  ) : null}
                  {website && String(website).trim() !== '' ? (
                    <a
                      href={website.startsWith('http') ? website : `https://${website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rwm-job-detail__browse-link text-decoration-none small"
                    >
                      Visit website →
                    </a>
                  ) : null}
                  {company.slug ? (
                    <div className="mt-2">
                      <Link
                        to={`/companies/${company.slug}`}
                        className="small rwm-job-detail__browse-link text-decoration-none"
                        data-testid="rwm-job-detail-company-profile-link"
                      >
                        View company profile →
                      </Link>
                    </div>
                  ) : null}
                  <HideCompanyControl
                    companyId={company.id}
                    token={token}
                    isCandidate={isCandidate}
                  />
                </Card.Body>
              </Card>
            </div>
          </Col>

          <Col lg={4}>
            <div
              className={`rwm-job-detail__apply-sidebar mb-4 ${
                job.application_method === 'rwm_flow' ? '' : 'd-none d-lg-block'
              }`}
            >
              <Card className="border rwm-job-detail__card" style={{ borderColor: 'var(--rwm-border)' }} data-testid="rwm-apply-card">
                <Card.Body className="p-4">
                  <ApplyCardContent
                    job={job}
                    slug={slug}
                    showHelper
                    applyButtonTestId={job.application_method === 'rwm_flow' ? null : 'rwm-apply-button'}
                  />
                </Card.Body>
              </Card>
            </div>

            <div className="d-none d-lg-block">
              <Card className="border mb-4 rwm-job-detail__card" style={{ borderColor: 'var(--rwm-border)' }} data-testid="rwm-job-info">
                <Card.Body className="p-4">
                  <h3 className="h6 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
                    Job Info
                  </h3>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>💰 Salary</span>
                    <span className="fw-medium text-end">{salaryText}</span>
                  </div>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>📋 Job Type</span>
                    <span className="text-end">{formatEmploymentTitle(job.employment_type)}</span>
                  </div>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>🎯 Seniority</span>
                    <span className="text-end">{formatSeniority(job.seniority_level)}</span>
                  </div>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>🌍 Location</span>
                    <span className="text-end">{locationLine}</span>
                  </div>
                  <div className="rwm-job-detail__info-row mb-2">
                    <span>📁 Category</span>
                    <span className="text-end">{category.name || '—'}</span>
                  </div>
                  <div className="rwm-job-detail__info-row">
                    <span>📅 Posted</span>
                    <span className="text-end">{formatPostedLong(job.approved_at)}</span>
                  </div>
                </Card.Body>
              </Card>
            </div>

            <div className="d-none d-lg-block">
              <Card className="border rwm-job-detail__card" style={{ borderColor: 'var(--rwm-border)' }} data-testid="rwm-company-card">
                <Card.Body className="p-4">
                  <h3 className="h6 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
                    About the Company
                  </h3>
                  <div className="d-flex gap-2 align-items-center mb-2">
                    <div style={{ width: 36, height: 36 }}>
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt=""
                          width={36}
                          height={36}
                          className="rounded-circle"
                          style={{ objectFit: 'cover', width: 36, height: 36 }}
                        />
                      ) : (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold small"
                          style={{
                            width: 36,
                            height: 36,
                            backgroundColor: avatarColor(company.name || ''),
                          }}
                        >
                          {initial.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <span className="fw-medium">{company.name}</span>
                  </div>
                  {employeeCountLabel(company.employee_count) ? (
                    <p className="small text-muted mb-2">{employeeCountLabel(company.employee_count)}</p>
                  ) : null}
                  {website && String(website).trim() !== '' ? (
                    <a
                      href={website.startsWith('http') ? website : `https://${website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rwm-job-detail__browse-link text-decoration-none small"
                    >
                      Visit website →
                    </a>
                  ) : null}
                  {company.slug ? (
                    <div className="mt-2">
                      <Link
                        to={`/companies/${company.slug}`}
                        className="small rwm-job-detail__browse-link text-decoration-none"
                        data-testid="rwm-job-detail-company-profile-link"
                      >
                        View company profile →
                      </Link>
                    </div>
                  ) : null}
                  <HideCompanyControl
                    companyId={company.id}
                    token={token}
                    isCandidate={isCandidate}
                  />
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </div>

      {job.application_method !== 'rwm_flow' ? (
        <div className="rwm-job-detail__apply-sticky-mobile d-lg-none">
          <ApplyActionButton cfg={getApplyConfig(job)} dataTestId="rwm-apply-button" />
        </div>
      ) : null}
      </div>
    </Container>
  );
}
